import _ from "lodash";
import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import * as Yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import CustomerLookupCard from "../../components/cards/CustomerLookupCard";
import AudioRecordButton from "../../components/form/AudioRecordButton";
import Input from "../../components/form/Input";
import ImageThumbnailGallery from "../../components/ImageThumbnailGallery";
import AssistantDialog from "../../components/modal/AssistantDialog";
import Markdown from "../../components/misc/Markdown";
import MobileActionBarContent from "../../components/mobile-action-bar/MobileActionBarContent";
import Spinner from "../../components/misc/Spinner";
import AttachmentUploader from "../../components/uploads/AttachmentUploader";
import { Role, useAuthStore } from "../../stores/auth";
import { WORK_ORDER_STATUS, useWorkOrdersStore } from "../../stores/work_orders";
import { useNatravStore } from "../../stores/natrav";
import { useSettingsStore } from "../../stores/settings";
import {
  getCustomerAddress,
  getCustomerProperties,
  getPropertyAddress,
  getPropertyCommunications,
  getPropertyContacts,
  searchCustomerByNumber,
} from "../../services/search";
import { getTechnicians } from "../../services/users";

const emptyAddress = { street: "", zip: "", city: "", district: "", country: "" };
const emptyEntity = { number: "", name: "", addresses: [emptyAddress] };
const initialUploadStatus = { isBusy: false, hasErrors: false };

const normalizeAddress = (address = {}) => ({
  street: address.street || "",
  zip: address.zip || "",
  city: address.city || "",
  district: address.district || "",
  country: address.country || "",
  state: address.state || "",
  country_code: address.country_code || "",
  lat: address.lat || "",
  lon: address.lon || "",
});

const normalizeEntity = (entity) => ({
  number: entity?.number || "",
  name: entity?.name || "",
  addresses: _.isEmpty(entity?.addresses) ? [emptyAddress] : [normalizeAddress(entity.addresses[0])],
});

const entityHasIdentifier = (entity) => {
  const address = entity?.addresses?.[0] || {};
  return [entity?.number, entity?.name, address.street, address.zip, address.city].some((v) => !!String(v || "").trim());
};

const createInitialValues = (workOrder) => ({
  version: workOrder?.version || 0,
  new_customer: !!workOrder && !workOrder.customer?.number,
  scheduled_from: workOrder?.scheduled_from || "",
  scheduled_until: workOrder?.scheduled_until || "",
  availability_from: workOrder?.availability_from?.slice(0, 5) || "",
  availability_until: workOrder?.availability_until?.slice(0, 5) || "",
  remark: workOrder?.remark || "",
  remark_summary: workOrder?.remark_summary || "",
  assigned_to_id: workOrder?.assigned_to?.id || "",
  customer: normalizeEntity(workOrder?.customer || emptyEntity),
  customer_property: normalizeEntity(workOrder?.customer_property || emptyEntity),
  uploads: [],
});

const createValidationSchema = (t) =>
  Yup.object().shape({
    customer: Yup.object()
      .shape({
        // Raw key: CustomerLookupCard translates field errors via t(msg).
        number: Yup.string().matches(/^\d+$/, {
          message: "work_orders.errors.customer_number_numeric",
          excludeEmptyString: true,
        }),
      })
      .test("customer-required", t("work_orders.errors.customer_required"), entityHasIdentifier),
    scheduled_until: Yup.string().test(
      "scheduled-until-after-from",
      t("work_orders.errors.scheduled_until_before_from"),
      function (value) {
        const { scheduled_from } = this.parent;
        return !value || !scheduled_from || value >= scheduled_from;
      },
    ),
    availability_until: Yup.string().test(
      "availability-until-after-from",
      t("work_orders.errors.availability_until_before_from"),
      function (value) {
        const { availability_from } = this.parent;
        return !value || !availability_from || value >= availability_from;
      },
    ),
  });

function WorkOrderAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const workOrderId = Number.parseInt(params.workOrderId);
  const isAddMode = !workOrderId;
  const navigate = useNavigate();
  const [technicians, setTechnicians] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyContacts, setPropertyContacts] = useState([]);
  const [propertyCommunications, setPropertyCommunications] = useState([]);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [isLoadingProperty, setIsLoadingProperty] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showRemarkPreview, setShowRemarkPreview] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("original");
  const [assistantFields, setAssistantFields] = useState({});
  const [uploadStatus, setUploadStatus] = useState(initialUploadStatus);
  const { me, hasRole } = useAuthStore(
    useShallow((state) => ({ me: state.me, hasRole: state.hasRole })),
  );
  const isBackoffice = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const features = useSettingsStore((state) => state.features);
  const { tunnelAvailable, fetchTunnelStatus } = useNatravStore(
    useShallow((state) => ({
      tunnelAvailable: state.available,
      fetchTunnelStatus: state.fetchStatus,
    })),
  );

  const { workOrdersByIds, getWorkOrderById, createOne, updateOne, deleteWorkOrderFile } = useWorkOrdersStore(
    useShallow((state) => ({
      workOrdersByIds: state.byIds,
      getWorkOrderById: state.getById,
      createOne: state.createOne,
      updateOne: state.updateOne,
      deleteWorkOrderFile: state.deleteFile,
    })),
  );
  const workOrder = Number.isInteger(workOrderId) ? workOrdersByIds[workOrderId] : null;
  const isLoading = !isAddMode && !workOrder;

  useEffect(() => {
    getTechnicians().then((users) => setTechnicians(users.filter((u) => u.active !== false)));
    if (features.handel) {
      fetchTunnelStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (workOrderId) {
      getWorkOrderById({ id: workOrderId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const initialValues = useMemo(
    () => createInitialValues(workOrder),
    [workOrder],
  );

  const applyEntityData = (prefix, currentValues, setFieldValue, data = { name: "", address: { street: "" } }) => {
    if (data.number !== undefined) {
      setFieldValue(`${prefix}.number`, data.number || currentValues.number || "");
    }
    setFieldValue(`${prefix}.name`, data.name || "");
    setFieldValue(`${prefix}.is_fetched`, data?.is_fetched || false);
    setFieldValue(`${prefix}.fetched_fields`, {
      name: !!data.name,
      street: !!data.address?.street,
      zip: !!data.address?.zip,
      city: !!data.address?.city,
    });
    setFieldValue(`${prefix}.addresses[0]`, {
      ...currentValues.addresses[0],
      street: data.address?.street || "",
      city: data.address?.city || "",
      zip: data.address?.zip || "",
      district: data.address?.district || "",
      country: data.address?.country?.name || data.address?.country || "",
    });
  };

  const resetFetchedData = (values, setFieldValue) => {
    applyEntityData("customer", values.customer, setFieldValue);
    applyEntityData("customer_property", values.customer_property, setFieldValue);
    setCustomerSearchError(null);
    setPropertyOptions([]);
    setPropertyContacts([]);
    setPropertyCommunications([]);
  };

  // Editing the customer number must not clear already entered data — only
  // the ERP-verified state is dropped so name and address keep their values
  // but become editable again.
  const clearFetchedState = (values, setFieldValue) => {
    ["customer", "customer_property"].forEach((prefix) => {
      if (values[prefix]?.is_fetched) {
        setFieldValue(`${prefix}.is_fetched`, false);
        setFieldValue(`${prefix}.fetched_fields`, {});
      }
    });
    setCustomerSearchError(null);
    setPropertyOptions([]);
    setPropertyContacts([]);
    setPropertyCommunications([]);
  };

  // Assistant results only fill fields that are still empty — anything the
  // user (or an ERP fetch) already filled is left untouched. Entities that
  // receive new values lose their ERP-fetched state so the fields stay
  // editable. The submitted text remains the original remark while the
  // generated summary is stored separately.
  const isBlank = (value) => !String(value || "").trim();

  // Every field the assistant writes is recorded in assistantFields and gets
  // the same yellow "prefilled, please review" background the delivery note
  // form uses. The highlight persists until the assistant runs again.
  const applyAssistantEntity = (prefix, values, setFieldValue, entity) => {
    if (!entity) {
      return {};
    }
    const current = values[prefix] || {};
    const skipNumber = prefix === "customer" && values.new_customer;
    const filled = {};
    if (entity.number && !skipNumber && isBlank(current.number)) {
      setFieldValue(`${prefix}.number`, entity.number);
      filled.number = true;
    }
    if (entity.name && isBlank(current.name)) {
      setFieldValue(`${prefix}.name`, entity.name);
      filled.name = true;
    }
    const address = entity.address || {};
    const currentAddress = current.addresses?.[0] || emptyAddress;
    if (address.street && isBlank(currentAddress.street)) filled.street = true;
    if (address.zip && isBlank(currentAddress.zip)) filled.zip = true;
    if (address.city && isBlank(currentAddress.city)) filled.city = true;
    const nextAddress = {
      ...currentAddress,
      ...(filled.street ? { street: address.street } : {}),
      ...(filled.zip ? { zip: address.zip } : {}),
      ...(filled.city ? { city: address.city } : {}),
    };
    if (!_.isEqual(nextAddress, currentAddress)) {
      setFieldValue(`${prefix}.addresses[0]`, nextAddress);
    }
    if (!_.isEmpty(filled)) {
      setFieldValue(`${prefix}.is_fetched`, false);
      setFieldValue(`${prefix}.fetched_fields`, {});
    }
    return filled;
  };

  const applyAssistantResult = (values, setFieldValue, data, sourceText) => {
    const filled = {};
    if (data.scheduled_from && isBlank(values.scheduled_from)) {
      setFieldValue("scheduled_from", data.scheduled_from);
      filled.scheduled_from = true;
    }
    if (data.scheduled_until && isBlank(values.scheduled_until)) {
      setFieldValue("scheduled_until", data.scheduled_until);
      filled.scheduled_until = true;
    }
    if (data.availability_from && isBlank(values.availability_from)) {
      setFieldValue("availability_from", data.availability_from.slice(0, 5));
      filled.availability_from = true;
    }
    if (data.availability_until && isBlank(values.availability_until)) {
      setFieldValue("availability_until", data.availability_until.slice(0, 5));
      filled.availability_until = true;
    }
    setFieldValue("remark", sourceText);
    setFieldValue("remark_summary", data.summary || "");
    setSelectedRemark(data.summary ? "summary" : "original");
    setShowRemarkPreview(Boolean(data.summary));
    if (data.summary) {
      filled.remark_summary = true;
    }
    filled.customer = applyAssistantEntity("customer", values, setFieldValue, data.customer);
    filled.customer_property = applyAssistantEntity("customer_property", values, setFieldValue, data.customer_property);
    setAssistantFields(filled);
    setShowAssistant(false);
  };

  const handleNewCustomerChange = (checked, values, setFieldValue) => {
    setFieldValue("new_customer", checked);
    if (!checked) {
      return;
    }
    setFieldValue("customer.number", "");
    if (features.handel) {
      resetFetchedData(values, setFieldValue);
    }
  };

  const selectCustomerProperty = async (currentValues, setFieldValue, options, number) => {
    const selected = options.find((o) => String(o.value.number) === String(number));
    const pick = selected || (options.length > 0 ? options[0] : null);
    if (!pick) {
      return;
    }
    setIsLoadingProperty(true);
    try {
      const [address, contacts, comms] = await Promise.all([
        getPropertyAddress(pick.value.slug),
        getPropertyContacts(pick.value.slug),
        getPropertyCommunications(pick.value.slug),
      ]);
      applyEntityData("customer_property", currentValues, setFieldValue, {
        is_fetched: true,
        number: pick.value.number,
        name: address?.name,
        address,
      });
      setPropertyContacts(contacts || []);
      setPropertyCommunications(comms || []);
    } catch {
      setPropertyContacts([]);
      setPropertyCommunications([]);
    } finally {
      setIsLoadingProperty(false);
    }
  };

  const runCustomerSearch = async (values, setFieldValue) => {
    if (_.isEmpty(values.customer.number)) return [];
    setIsSearchingCustomer(true);
    try {
      const { data } = await searchCustomerByNumber({ number: values.customer.number });
      if (_.isEmpty(data)) {
        applyEntityData("customer", values.customer, setFieldValue);
        setCustomerSearchError("customers.not_found");
        setPropertyOptions([]);
        setPropertyContacts([]);
        setPropertyCommunications([]);
        return [];
      }

      const customerId = data[0].slug;
      const [address, properties] = await Promise.all([
        getCustomerAddress(customerId),
        getCustomerProperties(customerId),
      ]);
      setCustomerSearchError(null);
      applyEntityData("customer", values.customer, setFieldValue, {
        is_fetched: true,
        name: address.name,
        address,
      });
      const options = (properties || []).map((p) => ({ value: p, label: p.number }));
      setPropertyOptions(options);
      await selectCustomerProperty(values.customer_property, setFieldValue, options, values.customer_property.number);
      return options;
    } catch {
      setCustomerSearchError("customers.service_unavailable");
      return [];
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (uploadStatus.isBusy || uploadStatus.hasErrors) {
      setSubmitting(false);
      return;
    }

    const payload = {
      version: values.version,
      scheduled_from: values.scheduled_from || null,
      scheduled_until: values.scheduled_until || null,
      availability_from: values.availability_from || null,
      availability_until: values.availability_until || null,
      remark: values.remark || "",
      remark_summary: values.remark_summary || null,
      assigned_to_id: values.assigned_to_id ? Number(values.assigned_to_id) : null,
      customer: normalizeEntity(values.customer),
      customer_property: entityHasIdentifier(values.customer_property) ? normalizeEntity(values.customer_property) : null,
      uploads: values.uploads,
    };
    const result = isAddMode
      ? await createOne(payload)
      : await updateOne({ id: workOrder.id, ...payload });
    setSubmitting(false);
    if (result.data) {
      navigate(`/work_orders/${result.data.id}/details`);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: createValidationSchema(t),
    onSubmit: handleSubmit,
  });
  const { errors, isSubmitting, setFieldValue, submitForm, values } = formik;

  if (!isBackoffice) {
    return (
      <div>
        <Breadcrumb items={[{ label: t("work_orders.work_orders"), to: "/work_orders" }, { label: t("permissions.can_not_edit") }]} />
        <div className="alert alert-warning">{t("permissions.can_not_edit")}</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <Breadcrumb items={[{ label: t("work_orders.work_orders"), to: "/work_orders" }, { label: "…" }]} />
        <Spinner />
      </div>
    );
  }

  const activeStatuses = [WORK_ORDER_STATUS.OPEN, WORK_ORDER_STATUS.IN_PROGRESS];
  if (!isAddMode && !activeStatuses.includes(workOrder.status)) {
    return (
      <div>
        <Breadcrumb items={[{ label: t("work_orders.work_orders"), to: "/work_orders" }, { label: `${t("work_orders.number")} ${workOrder.number}` }]} />
        <div className="alert alert-warning">{t("work_orders.read_only")}</div>
        <Link to={`/work_orders/${workOrder.id}/details`} className="btn btn-default">
          {t("common.back")}
        </Link>
      </div>
    );
  }

  const hasRemarkSummary = Boolean(values.remark_summary);
  const showRemarkSummary = hasRemarkSummary && selectedRemark !== "original";
  const activeRemarkName = showRemarkSummary ? "remark_summary" : "remark";
  const activeRemark = values[activeRemarkName];
  const originalRemarkButtonClass = `btn btn-xs btn-default${showRemarkSummary ? "" : " active"}`;
  const summaryRemarkButtonClass = `btn btn-xs btn-default${showRemarkSummary ? " active" : ""}`;
  const remarkHighlightClass = assistantFields.remark_summary && showRemarkSummary
    ? " bg-yellow-100 bg-opacity-25"
    : "";
  const customerError = typeof errors.customer === "string" ? errors.customer : null;
  const uploadBlocksSave = uploadStatus.isBusy || uploadStatus.hasErrors;
  const saveDisabled = isSubmitting || uploadBlocksSave;

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("work_orders.work_orders"), to: "/work_orders" },
          { label: isAddMode ? t("work_orders.add") : `${t("work_orders.number")} ${workOrder.number}` },
        ]}
      />

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
            <div className="card border-0 mb-4">
              <div className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center">
                <i className="fa fa-clipboard-list fa-lg fa-fw text-dark text-opacity-50 me-1" />
                {t("work_orders.work_order")}
                {features.assistant && (
                  <button
                    type="button"
                    className="btn btn-sm btn-info ms-2"
                    onClick={() => setShowAssistant(true)}
                  >
                    <i className="fa fa-wand-magic-sparkles fa-fw me-1" />
                    {t("assistant.button")}
                  </button>
                )}
              </div>
              <div className="card-body">
                <div className="row gx-3 gx-lg-4">
                  <div className="col-12 col-lg-6 col-xl-4">
                    <div className="row mb-3 gx-3">
                      <div className="col-6">
                        <label className="form-label" htmlFor="scheduled_from">{t("work_orders.scheduled_from")}</label>
                        <Input field={formik.getFieldProps("scheduled_from")} form={formik} type="date" className={`form-control fs-13px h-45px${assistantFields.scheduled_from ? " bg-yellow-100 bg-opacity-25" : ""}`} />
                      </div>
                      <div className="col-6">
                        <label className="form-label" htmlFor="scheduled_until">{t("work_orders.scheduled_until")}</label>
                        <Input field={formik.getFieldProps("scheduled_until")} form={formik} type="date" className={`form-control fs-13px h-45px${assistantFields.scheduled_until ? " bg-yellow-100 bg-opacity-25" : ""}`} />
                        {errors.scheduled_until && <div className="invalid-feedback d-block">{errors.scheduled_until}</div>}
                      </div>
                    </div>
                    <div className="row mb-3 gx-3">
                      <div className="col-6">
                        <label className="form-label" htmlFor="availability_from">{t("work_orders.availability_from")}</label>
                        <Input field={formik.getFieldProps("availability_from")} form={formik} type="time" className={`form-control fs-13px h-45px${assistantFields.availability_from ? " bg-yellow-100 bg-opacity-25" : ""}`} />
                      </div>
                      <div className="col-6">
                        <label className="form-label" htmlFor="availability_until">{t("work_orders.availability_until")}</label>
                        <Input field={formik.getFieldProps("availability_until")} form={formik} type="time" className={`form-control fs-13px h-45px${assistantFields.availability_until ? " bg-yellow-100 bg-opacity-25" : ""}`} />
                        {errors.availability_until && <div className="invalid-feedback d-block">{errors.availability_until}</div>}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="assigned_to_id">{t("work_orders.assigned_to")}</label>
                      <select {...formik.getFieldProps("assigned_to_id")} className="form-select fs-13px h-45px">
                        <option value="">{t("work_orders.unassigned")}</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.name}{tech.number ? ` (${tech.number})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6 col-xl-8 d-flex flex-column">
                    <div className="mb-3 d-flex flex-column flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        <label className="form-label mb-0" htmlFor={activeRemarkName}>{t("work_orders.remark")}</label>
                        {hasRemarkSummary && (
                          <div className="btn-group ms-2" role="group" aria-label={t("work_orders.remark")}>
                            <button
                              type="button"
                              className={originalRemarkButtonClass}
                              aria-pressed={!showRemarkSummary}
                              onClick={() => {
                                setSelectedRemark("original");
                                setShowRemarkPreview(false);
                              }}
                            >
                              {t("work_orders.remark_original")}
                            </button>
                            <button
                              type="button"
                              className={summaryRemarkButtonClass}
                              aria-pressed={showRemarkSummary}
                              onClick={() => {
                                setSelectedRemark("summary");
                                setShowRemarkPreview(true);
                              }}
                            >
                              {t("work_orders.remark_summary")}
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          className={`btn btn-xs btn-default ms-2${showRemarkPreview ? " active" : ""}`}
                          title={showRemarkPreview ? t("markdown.edit") : t("markdown.preview")}
                          onClick={() => setShowRemarkPreview(!showRemarkPreview)}
                        >
                          <i className={`fa ${showRemarkPreview ? "fa-pen" : "fa-eye"} fa-fw`} />
                        </button>
                      </div>
                      {showRemarkPreview ? (
                        <div className={`form-control fs-15px flex-grow-1${remarkHighlightClass}`} style={{ minHeight: "12rem" }}>
                          <Markdown text={activeRemark} />
                        </div>
                      ) : (
                        <div className="input-group flex-grow-1">
                          <textarea
                            {...formik.getFieldProps(activeRemarkName)}
                            id={activeRemarkName}
                            rows={8}
                            className={`form-control fs-13px${remarkHighlightClass}`}
                          />
                          {!showRemarkSummary && (
                            <AudioRecordButton
                              className="d-md-none"
                              onTranscription={(text) => {
                                const current = values.remark || "";
                                setFieldValue("remark", current ? `${current} ${text}` : text);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {features.handel && tunnelAvailable === false && (
              <div className="alert alert-warning mb-3">
                <i className="fa fa-exclamation-triangle me-2" />
                {t("customers.service_unavailable")}
              </div>
            )}

            <div className="row gx-0 gx-lg-4">
              <div className="col-12 col-lg">
                <CustomerLookupCard
                  icon="fa-user"
                  title={t("customers.customer")}
                  fieldPrefix="customer"
                  values={values.customer}
                  setFieldValue={setFieldValue}
                  isSearching={isSearchingCustomer}
                  isLoading={isSearchingCustomer}
                  searchError={customerSearchError}
                  fetchedFields={values.customer?.fetched_fields}
                  highlightedFields={assistantFields.customer}
                  numberLabel={t("customers.number")}
                  nameLabel={t("customers.name")}
                  nameDisabled={!!values.customer?.fetched_fields?.name}
                  numberDisabled={values.new_customer}
                  numberCheckbox={{
                    label: t("customers.new_customer"),
                    checked: values.new_customer,
                    onChange: (e) => handleNewCustomerChange(e.target.checked, values, setFieldValue),
                  }}
                  onNumberChange={(e) => {
                    setFieldValue("customer.number", e.target.value);
                    if (features.handel) {
                      clearFetchedState(values, setFieldValue);
                    }
                  }}
                  searchDisabled={!features.handel || values.new_customer}
                  searchDisabledTooltip={features.handel ? "" : t("customers.search_unavailable")}
                  onSearch={() => runCustomerSearch(values, setFieldValue)}
                />
                {customerError && <div className="alert alert-danger">{customerError}</div>}
              </div>
              <div className="col-12 col-lg">
                <CustomerLookupCard
                  icon="fa-building"
                  title={t("customers.properties.property")}
                  fieldPrefix="customer_property"
                  values={values.customer_property}
                  setFieldValue={setFieldValue}
                  isLoading={isLoadingProperty}
                  fetchedFields={values.customer_property?.fetched_fields}
                  highlightedFields={assistantFields.customer_property}
                  propertyOptions={propertyOptions}
                  numberLabel={t("customers.properties.number")}
                  nameLabel={t("customers.properties.name")}
                  nameDisabled={propertyOptions.length > 0}
                  contacts={propertyContacts}
                  communications={propertyCommunications}
                  onSelectOption={(selectedNumber) =>
                    selectCustomerProperty(values.customer_property, setFieldValue, propertyOptions, selectedNumber)
                  }
                />
              </div>
            </div>

            <div className="row gx-0">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-paperclip fa-lg fa-fw text-dark text-opacity-50 me-1" />
                    {t("menu.files")}
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col">
                        <AttachmentUploader
                          metadata={{ userId: me.id }}
                          onUploadsChange={(uploads) =>
                            setFieldValue("uploads", uploads, false)
                          }
                          onStatusChange={setUploadStatus}
                        />
                      </div>
                    </div>
                    {!isAddMode && (
                      <div className="row mt-3">
                        <div className="col">
                          <div className="float-start">
                            <ImageThumbnailGallery
                              files={workOrder.files}
                              onDelete={(fileId) => deleteWorkOrderFile({ id: workOrder.id, fileId })}
                              canDelete={(file) => file.created_by?.id === me.id}
                              showFileDetails={false}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row gx-0 d-none d-md-flex">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-body">
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-success flex-grow-1" disabled={saveDisabled}>
                        <i className={`fa fa-${isSubmitting ? "spinner fa-spin" : "save"} fa-fw me-1`} />
                        {t("common.save")}
                      </button>
                      <Link
                        to={isAddMode ? "/work_orders" : `/work_orders/${workOrder.id}/details`}
                        className="btn btn-danger"
                        title={t("common.close")}
                      >
                        <i className="fa fa-times" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <MobileActionBarContent>
              <div className="d-flex gap-2">
                <button type="button" onClick={submitForm} className="btn btn-success flex-fill" disabled={saveDisabled}>
                  <i className={`fa fa-${isSubmitting ? "spinner fa-spin" : "save"} fa-fw me-1`} />
                  {t("common.save")}
                </button>
                <Link
                  to={isAddMode ? "/work_orders" : `/work_orders/${workOrder.id}/details`}
                  className="btn btn-danger"
                  title={t("common.close")}
                >
                  <i className="fa fa-times" />
                </Link>
              </div>
            </MobileActionBarContent>

            {showAssistant && (
              <AssistantDialog
                initialText={values.remark}
                onApply={(data, sourceText) => applyAssistantResult(values, setFieldValue, data, sourceText)}
                onClose={() => setShowAssistant(false)}
              />
            )}
        </form>
      </FormikProvider>
    </div>
  );
}

export default WorkOrderAddEdit;
